'use client';
import React, { useEffect, useState } from 'react';
import { RecentlyViewedProducts as SharedRecentlyViewedProducts } from '@launchstore/shared-puck';
import type { RecentlyViewedProductsProps } from '@launchstore/shared-puck';
import { getRegion } from '@/lib/data/regions';
import { listProducts } from '@/lib/data/products';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { recordRecentlyViewed, getRecentlyViewedIds } from '@/lib/util/recently-viewed';
import TemplateProductCard from '@/modules/products/components/product-preview/template-product-card';
import type { CarouselProduct } from '@launchstore/shared-puck';

const toCarouselProduct = (p: any): CarouselProduct => ({
  id: p.id, title: p.title, handle: p.handle, thumbnail: p.thumbnail, images: p.images, variants: p.variants,
});

const SharedRecentlyViewedProductsRender = (SharedRecentlyViewedProducts as any).render;

const RecentlyViewedProductsImpl: React.FC<RecentlyViewedProductsProps> = (props: any) => {
  const { context } = usePuckContext() as any;
  const countryCode = context?.countryCode;
  const cardTemplate = context?.productCardTemplate;
  const currentProductId = context?.product?.id;
  // History lives in localStorage (client-only, standard approach). The puck
  // context field is the SSR-friendly future path — nothing injects it today,
  // so fall back to reading storage directly or the component renders nothing.
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>(context?.recentlyViewedIds || []);
  const [reg, setReg] = useState<any>(null);
  const [products, setProducts] = useState<CarouselProduct[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Record this product as viewed, then show the shopper's OTHER recently
    // viewed products — the current product never appears in its own strip.
    if (currentProductId) recordRecentlyViewed(currentProductId);
    setRecentlyViewedIds(
      getRecentlyViewedIds().filter((id) => id !== currentProductId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProductId]);

  useEffect(() => {
    if (!countryCode || recentlyViewedIds.length === 0) { setLoaded(true); return; }
    let mounted = true;
    (async () => {
      try {
        const region = await getRegion(countryCode);
        if (mounted && region) setReg(region);
        if (!region) { setLoaded(true); return; }
        const ids = recentlyViewedIds.slice(0, props.maxProducts || 8);
        const { response } = await listProducts({
          queryParams: { region_id: region.id, id: ids, fields: '*variants.calculated_price' },
          countryCode,
        });
        const raw = response.products;
        // Preserve recentlyViewedIds order
        const sorted = ids.map((id) => raw.find((p: any) => p.id === id)).filter(Boolean);
        if (mounted) setProducts(sorted.map(toCarouselProduct));
      } catch {}
      if (mounted) setLoaded(true);
    })();
    return () => { mounted = false; };
  }, [countryCode, JSON.stringify(recentlyViewedIds), props.maxProducts]);

  if (!loaded) return <></>;
  // Same guard the collection grid uses: only render template cards when the
  // PRODUCT_CARD template actually has content — otherwise fall back to the
  // shared built-in card instead of rendering empty cells.
  const canUseTemplateCard = !!(cardTemplate?.puckData?.content?.length && reg);
  const renderProduct =
    canUseTemplateCard
      ? (p: any) => (
          <TemplateProductCard product={p as any} region={reg} productCardTemplate={cardTemplate} countryCode={countryCode} />
        )
      : undefined;
  return (
    <SharedRecentlyViewedProductsRender {...(props as any)} products={products} renderProduct={renderProduct} />
  );
};

export const RecentlyViewedProducts: any = { ...SharedRecentlyViewedProducts, render: (props: any) => <RecentlyViewedProductsImpl {...props} /> };
export default RecentlyViewedProducts;
